/**
 * BitacoraLinkModal Component
 *
 * Modal to link a conversation to the project bitacora/timeline.
 * Allows selecting a project and providing a summary.
 *
 * @module components/chat/BitacoraLinkModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { linkToBitacora } from '../../services/chatService';
import { supabase } from '../../services/supabase';

const BitacoraLinkModal = ({ show, onHide, conversation, onLinked }) => {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [summary, setSummary] = useState('');
  const [messageRange, setMessageRange] = useState('');

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    const { data, error } = await supabase
      .from('projects')
      .select('id, title, project_number')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setProjects(data);
    }
    setLoadingProjects(false);
  }, []);

  useEffect(() => {
    if (show) {
      loadProjects();
      // Pre-select project if conversation is a channel with project
      if (conversation?.project_id) {
        setProjectId(conversation.project_id);
      }
    }
  }, [show, conversation, loadProjects]);

  const resetForm = () => {
    setProjectId(conversation?.project_id || '');
    setSummary('');
    setMessageRange('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectId) {
      toast.warning('Selecciona un proyecto');
      return;
    }
    if (!summary.trim()) {
      toast.warning('El resumen es requerido');
      return;
    }

    setSubmitting(true);
    try {
      // The "rango de mensajes" note used to be collected and then dropped on
      // the floor — it was never passed to linkToBitacora, so whatever the
      // user typed there vanished on submit. `chat_bitacora_links` has no
      // column for a free-text range (only from_message_id/to_message_id, and
      // this modal has no message picker), so the note is folded into the
      // summary, which is the field the bitacora actually renders.
      const trimmedRange = messageRange.trim();
      const finalSummary = trimmedRange
        ? `${summary.trim()}\n\nRango: ${trimmedRange}`
        : summary.trim();

      const { data, error } = await linkToBitacora({
        conversation_id: conversation.id,
        project_id: projectId,
        summary: finalSummary,
      });

      if (error) throw error;

      toast.success('Conversación vinculada a bitácora');
      if (onLinked) onLinked(data);
      handleClose();
    } catch (err) {
      toast.error('Error al vincular: ' + (err.message || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-journal-bookmark me-2" />
          Vincular a Bitácora
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Proyecto</Form.Label>
            {loadingProjects ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <Form.Select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                required
              >
                <option value="">Seleccionar proyecto...</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.project_number ? `${p.project_number} - ` : ''}{p.title}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Resumen <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe brevemente el contenido relevante de la conversación..."
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              required
            />
            <Form.Text className="text-muted">
              Este resumen aparecerá en la bitácora del proyecto.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Rango de mensajes (opcional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="ej. Mensajes del 14/02 sobre presupuesto"
              value={messageRange}
              onChange={(e) => setMessageRange(e.target.value)}
            />
            <Form.Text className="text-muted">
              Nota descriptiva sobre que mensajes aplican.
            </Form.Text>
          </Form.Group>

          <div className="d-grid">
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <><Spinner animation="border" size="sm" className="me-2" />Vinculando...</>
              ) : (
                <><i className="bi bi-journal-bookmark me-2" />Vincular a Bitácora</>
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

BitacoraLinkModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  conversation: PropTypes.shape({
    id: PropTypes.string,
    project_id: PropTypes.string,
  }),
  onLinked: PropTypes.func,
};

export default BitacoraLinkModal;
